import { extractMainDomain } from "./pickLandingDomain";
import { parseGateTimestamp } from "./subdomainLinkExpiry";

/**
 * 永久域 302 → t{ts} 落地（滑块门）；滑块通过后 → n{同ts}/#/entry（导航页）
 */
function isIpOrLocalhost(host) {
  return (
    host === "localhost" ||
    /^(\d{1,3}\.){3}\d{1,3}$/.test(host)
  );
}

/** 从当前页 href 读取 agent / agentId / agent_id */
export function getAgentFromCurrentHref() {
  const href = decodeURIComponent(window.location.href);
  const keys = ["agent", "agentId", "agent_id"];
  const segments = [];

  const hashIdx = href.indexOf("#");
  const searchIdx = href.indexOf("?");

  if (searchIdx >= 0) {
    const end = hashIdx >= 0 && hashIdx > searchIdx ? hashIdx : href.length;
    segments.push(href.slice(searchIdx + 1, end));
  }
  if (hashIdx >= 0) {
    const hashPart = href.slice(hashIdx + 1);
    const qInHash = hashPart.indexOf("?");
    if (qInHash >= 0) segments.push(hashPart.slice(qInHash + 1));
  }

  for (const seg of segments) {
    const params = new URLSearchParams(seg);
    for (const key of keys) {
      const v = params.get(key);
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
  }
  return null;
}

function applyAntiBotParams(params, antiBot) {
  if (!antiBot) return;
  if (antiBot.hitModules?.length) {
    params.set("_m", antiBot.hitModules.join(","));
  }
  if (antiBot.totalScore != null) {
    params.set("_r", String(antiBot.totalScore));
  }
}

/** 任意绝对/相对 H5 地址上追加 agent、_m、_r */
export function appendAntiBotToHref(href, options = {}) {
  const trimmed = String(href || "").trim();
  if (!trimmed) return trimmed;
  try {
    const url = new URL(trimmed, window.location.href);
    return appendParamsToHref(url, {
      agent: options.agent ?? getAgentFromCurrentHref(),
      antiBot: options.antiBot ?? null,
    });
  } catch {
    return trimmed;
  }
}

/** 在目标 URL 上追加 agent、_m、_r，不改 hash 路径 */
function appendParamsToHref(url, { agent, antiBot }) {
  if (url.hash && url.hash.length > 1) {
    const hashContent = url.hash.slice(1);
    const qIdx = hashContent.indexOf("?");
    const hashPath = qIdx >= 0 ? hashContent.slice(0, qIdx) : hashContent;
    const params = new URLSearchParams(
      qIdx >= 0 ? hashContent.slice(qIdx + 1) : ""
    );
    params.delete("t");
    params.delete("T");
    params.delete("f");
    params.delete("F");
    if (agent && !params.has("agent")) params.set("agent", String(agent));
    applyAntiBotParams(params, antiBot);
    const path = hashPath.startsWith("/") ? hashPath : `/${hashPath}`;
    const qs = params.toString();
    return `${url.origin}${url.pathname}#${path}${qs ? `?${qs}` : ""}`;
  }

  const params = new URLSearchParams(url.search);
  params.delete("t");
  params.delete("T");
  params.delete("f");
  params.delete("F");
  if (agent && !params.has("agent")) params.set("agent", String(agent));
  applyAntiBotParams(params, antiBot);
  const qs = params.toString();
  return `${url.origin}${url.pathname}${qs ? `?${qs}` : ""}`;
}

/**
 * 滑块通过：当前 t{ts} 子域 → n{同时间戳} 主域 /#/entry
 */
export function buildEntryRedirectUrl(options = {}) {
  const host = window.location.hostname;
  const mainHost = extractMainDomain(host);
  const gateParsed = parseGateTimestamp(host);
  const ts = gateParsed?.tsSec ?? Math.floor(Date.now() / 1000);
  const agent =
    options.agentId || getAgentFromCurrentHref() || null;
  const antiBot = options.antiBot || null;
  const extraQuery = options.extraQuery || "";

  const origin =
    window.location.protocol === "http:" ? "http:" : "https:";
  const base = new URL(`${origin}//${mainHost}/`);
  if (isIpOrLocalhost(mainHost) && window.location.port) {
    base.port = window.location.port;
  }
  base.hostname = `n${ts}.${mainHost}`;

  const params = new URLSearchParams();
  if (agent) params.set("agent", String(agent));
  applyAntiBotParams(params, antiBot);

  if (extraQuery) {
    const extra = new URLSearchParams(extraQuery);
    extra.forEach((value, key) => {
      if (!params.has(key)) params.set(key, value);
    });
  }
  const qs = params.toString();
  base.hash = qs ? `#/entry?${qs}` : "#/entry";

  return base.href;
}

/** 失效页「返回主站」：当前主域下新的 t{时间戳} 滑块门（保留 agent） */
export function buildFreshGateUrl() {
  const ts = Math.floor(Date.now() / 1000);
  const mainHost = extractMainDomain(window.location.hostname);
  const agent = getAgentFromCurrentHref();
  const protocol = window.location.protocol === "http:" ? "http:" : "https:";
  const url = new URL(`${protocol}//${mainHost}/`);
  if (isIpOrLocalhost(mainHost) && window.location.port) {
    url.port = window.location.port;
  }
  url.hostname = `t${ts}.${mainHost}`;
  if (agent) url.searchParams.set("agent", String(agent));
  return url.href;
}

export { extractMainDomain };
